import type { ButtonHTMLAttributes, ReactNode } from 'react'
import cn from 'classnames'

import { Spinner } from '@/UI-KIT/Spinner/Spinner'

import styles from './Button.module.scss'

/**
 * The platform draws every button on 8px corners — an earlier pass made them pills off a scaled
 * screenshot, and pills were simply a different product. Two primaries exist because the platform
 * has two: the nav primary is the brand blue, the page primary (the big CTA under a headline) is
 * black.
 */
export type ButtonVariant = 'solid' | 'outline' | 'ghost'
export type ButtonColorScheme = 'black' | 'blue' | 'white' | 'grey' | 'red' | 'brand' | 'gold'
export type ButtonSize = 'sm' | 'md' | 'lg'

type ButtonProps = {
  variant?: ButtonVariant
  colorScheme?: ButtonColorScheme
  size?: ButtonSize
  loading?: boolean
  fullWidth?: boolean
  startIcon?: ReactNode
  endIcon?: ReactNode
  children?: ReactNode
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'color'>

export const Button = ({
  variant = 'solid',
  colorScheme = 'black',
  size = 'md',
  loading = false,
  fullWidth = false,
  startIcon,
  endIcon,
  children,
  className,
  disabled,
  type = 'button',
  ...rest
}: ButtonProps) => (
  <button
    type={type}
    // A loading button that stays clickable is how a paid call gets fired twice.
    disabled={disabled || loading}
    className={cn(
      styles.root,
      styles[variant],
      styles[`scheme-${colorScheme}`],
      styles[`size-${size}`],
      className,
      { [styles.fullWidth as string]: fullWidth, [styles.loading as string]: loading },
    )}
    {...rest}
  >
    {loading ? <Spinner size={size === 'lg' ? 18 : 14} /> : startIcon}
    {children ? <span className={styles.label}>{children}</span> : null}
    {endIcon}
  </button>
)
